// ============= Imports =============
import { useState, useCallback, useRef } from 'react';
import { AppState, AppStateHook } from '../types';

// ============= Constants =============
/**
 * Initial state for the application
 */
const INITIAL_APP_STATE: AppState = {
  selectedUser: undefined,
  email: '',
  error: null,
  isLoading: false,
  showSuccessMessage: false,
};

// ============= Hook =============
/**
 * Custom hook to manage application state
 * @param successMessageDuration - Duration in ms to show success message (default: 3000ms)
 * @returns AppStateHook object containing state and state management functions
 */
export const useAppState = (successMessageDuration = 3000): AppStateHook => {
  // State initialization
  const [state, setState] = useState<AppState>(INITIAL_APP_STATE);
  const isMounted = useRef(true);

  /**
   * Resets the app state to initial values while preserving other state properties
   */
  const resetAppState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedUser: undefined,
      email: '',
      showSuccessMessage: true,
    }));
  }, []);

  /**
   * Shows success message for specified duration
   * Handles cleanup if component unmounts
   */
  const showSuccess = useCallback(() => {
    if (isMounted.current) {
      setState((prev) => ({
        ...prev,
        showSuccessMessage: true,
      }));

      setTimeout(() => {
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            showSuccessMessage: false,
          }));
        }
      }, successMessageDuration);
    }
  }, [successMessageDuration]);

  return {
    state,
    setState,
    isMounted,
    resetAppState,
    showSuccess,
  };
};
